import React, { useState, useEffect } from 'react';
import { boutiquesAPI } from '../../services/api';
import { Plus, Edit, Trash2, X, Store } from 'lucide-react';

function Boutiques() {
  const [boutiques, setBoutiques] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBoutique, setEditingBoutique] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    type: 'boutique',
    adresse: '',
    telephone: '',
    email: '',
    actif: true
  });

  useEffect(() => {
    loadBoutiques();
  }, []);

  const loadBoutiques = async () => {
    try {
      const response = await boutiquesAPI.getAll();
      setBoutiques(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBoutique) {
        await boutiquesAPI.update(editingBoutique.id, formData);
      } else {
        await boutiquesAPI.create(formData);
      }
      loadBoutiques();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Désactiver cette boutique ?')) {
      try {
        await boutiquesAPI.delete(id);
        loadBoutiques();
      } catch (error) {
        alert(error.response?.data?.error || 'Erreur');
      }
    }
  };

  const openModal = (boutique = null) => {
    if (boutique) {
      setEditingBoutique(boutique);
      setFormData(boutique);
    } else {
      setEditingBoutique(null);
      setFormData({
        nom: '',
        type: 'boutique',
        adresse: '',
        telephone: '',
        email: '',
        actif: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBoutique(null);
  };

  const getTypeBadge = (type) => {
    const colors = {
      boutique: 'bg-blue-100 text-blue-800',
      atelier: 'bg-purple-100 text-purple-800',
      depot: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[type]}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-2">
          <Store className="h-8 w-8" />
          <span>Gestion des boutiques</span>
        </h2>
        <button
          onClick={() => openModal()}
          className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
        >
          <Plus className="h-5 w-5" />
          <span>Ajouter une boutique</span>
        </button>
      </div>

      {/* Table desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {boutiques.map((boutique) => (
              <tr key={boutique.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{boutique.nom}</td>
                <td className="px-6 py-4 text-sm">{getTypeBadge(boutique.type)}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{boutique.adresse || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{boutique.telephone || '-'}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    boutique.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {boutique.actif ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-medium">
                  <button
                    onClick={() => openModal(boutique)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(boutique.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards mobile */}
      <div className="md:hidden space-y-3">
        {boutiques.map((boutique) => (
          <div key={boutique.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-400">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold text-gray-900">{boutique.nom}</div>
                <div className="text-xs text-gray-500">{boutique.adresse || '-'}</div>
              </div>
              {getTypeBadge(boutique.type)}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span>{boutique.telephone || '-'}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                boutique.actif ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {boutique.actif ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openModal(boutique)}
                className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium"
              >
                Modifier
              </button>
              <button
                onClick={() => handleDelete(boutique.id)}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 sm:p-8 max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800">
                {editingBoutique ? 'Modifier la boutique' : 'Nouvelle boutique'}
              </h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="boutique">Boutique</option>
                    <option value="atelier">Atelier</option>
                    <option value="depot">Dépôt</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <textarea
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {editingBoutique && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.actif}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Boutique active</label>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingBoutique ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Boutiques;
